import { useState, useEffect, useCallback } from 'react';
import useSubformApi from '../../api/subforms/useSubformApi';
import { convertToFormCrafterSchema } from '../../../utils/formCrafter/schemaUtils';

/**
 * useSubformPreview Hook
 *
 * Feature hook that fetches a subform and parses its schema for read-only preview.
 * Composes useSubformApi (API layer) + convertToFormCrafterSchema (schema parsing).
 *
 * @param {number|string|null} subformId - The subform ID to preview (null = inactive)
 * @returns {Object} - { subform, schema, linkedForms, loading }
 */
const useSubformPreview = (subformId) => {
  const { getSubform } = useSubformApi();
  const [subform, setSubform] = useState(null);
  const [schema, setSchema] = useState(null);
  const [linkedForms, setLinkedForms] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchSubform = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const data = await getSubform(id);
        setSubform(data);
        setLinkedForms(data.forms || []);

        if (data.json_object) {
          const parsed = convertToFormCrafterSchema(data.json_object, id);
          setSchema(parsed);
        } else {
          setSchema(null);
        }
      } catch (error) {
        console.error('Failed to fetch subform preview:', error);
        setSubform(null);
        setSchema(null);
        setLinkedForms([]);
      } finally {
        setLoading(false);
      }
    },
    [getSubform]
  );

  useEffect(() => {
    if (subformId) {
      fetchSubform(subformId);
    } else {
      setSubform(null);
      setSchema(null);
      setLinkedForms([]);
    }
  }, [subformId, fetchSubform]);

  return { subform, schema, linkedForms, loading };
};

export default useSubformPreview;
